import type { RoundStatus, RoundSummary } from '@/lib/api/types';
import { t, type PlainMessageKey } from '@/lib/i18n';

/**
 * Client-side filtering.
 *
 * `GET /rounds` only accepts `status` — period, course and hole-count are not server-side
 * filters, so they run here over the full round set the app already caches for statistics.
 */

export type PeriodKey = '30d' | '6m' | 'year' | 'all';
export type HolesKey = 9 | 18 | 'both';

export interface RoundFilters {
  status: RoundStatus | 'all';
  period: PeriodKey;
  holes: HolesKey;
  courseId: string | 'all';
}

export const DEFAULT_FILTERS: RoundFilters = {
  status: 'completed',
  period: 'all',
  holes: 'both',
  courseId: 'all',
};

export const PERIOD_LABEL: Record<PeriodKey, PlainMessageKey> = {
  '30d': 'rounds.filter.period.30d',
  '6m': 'rounds.filter.period.6m',
  year: 'rounds.filter.period.year',
  all: 'rounds.filter.period.all',
};

export const STATUS_LABEL: Record<RoundStatus | 'all', PlainMessageKey> = {
  all: 'rounds.filter.status.all',
  completed: 'rounds.filter.status.completed',
  in_progress: 'rounds.filter.status.inProgress',
  abandoned: 'rounds.filter.status.abandoned',
};

function periodStart(period: PeriodKey, now: Date): Date | null {
  const d = new Date(now);
  switch (period) {
    case '30d':
      d.setDate(d.getDate() - 30);
      return d;
    case '6m':
      d.setMonth(d.getMonth() - 6);
      return d;
    case 'year':
      return new Date(now.getFullYear(), 0, 1);
    case 'all':
      return null;
  }
}

/** A round's own date: when it finished, or when it started if it never did. */
export function roundDate(r: RoundSummary): Date {
  return new Date(r.completedAt ?? r.startedAt);
}

export function applyFilters(
  rounds: RoundSummary[],
  filters: RoundFilters,
  now = new Date(),
): RoundSummary[] {
  const start = periodStart(filters.period, now);

  return rounds.filter((r) => {
    if (filters.status !== 'all' && r.status !== filters.status) return false;
    if (filters.holes !== 'both' && r.holesPlayed !== filters.holes) return false;
    if (filters.courseId !== 'all' && r.courseId !== filters.courseId) return false;
    if (start && roundDate(r) < start) return false;
    return true;
  });
}

export function isDefaultFilters(f: RoundFilters): boolean {
  return (
    f.status === DEFAULT_FILTERS.status &&
    f.period === DEFAULT_FILTERS.period &&
    f.holes === DEFAULT_FILTERS.holes &&
    f.courseId === DEFAULT_FILTERS.courseId
  );
}

/**
 * A human sentence for the empty state, so it explains *which* filter emptied the list
 * rather than just saying nothing was found.
 */
export function describeFilters(f: RoundFilters): string {
  const status = f.status !== 'all' ? t(STATUS_LABEL[f.status]).toLowerCase() : null;
  const holes = f.holes !== 'both' ? String(f.holes) : null;
  const period = f.period !== 'all' ? t(PERIOD_LABEL[f.period]).toLowerCase() : null;

  if (status && holes && period) return t('rounds.empty.statusHolesPeriod', { status, holes, period });
  if (status && holes) return t('rounds.empty.statusHoles', { status, holes });
  if (status && period) return t('rounds.empty.statusPeriod', { status, period });
  if (holes && period) return t('rounds.empty.holesPeriod', { holes, period });
  if (status) return t('rounds.empty.status', { status });
  if (holes) return t('rounds.empty.holes', { holes });
  if (period) return t('rounds.empty.period', { period });
  return t('rounds.empty.none');
}
